import { useEffect, useMemo, useRef, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { BiGridVertical } from "react-icons/bi";
import theme from "../../../theme";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H2 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import { Image } from "../../../components/Image";
import Select from "../../../components/Select";
import { Input } from "../../../components/Input";
import { Slider } from "../../../components/Slider";
import { Modal } from "../../../components/Modal";
import editIcon from "../../../assets/settings/edit.svg";
import { useEntityTypes } from "../../../data/useEntityTypes"; // read list
import { useCreateEntityType } from "../../../data/useCreateEntityType";
import { useEditEntityType } from "../../../data/useEditEntityType";
import { useDeleteEntityType } from "../../../data/useDeleteEntityType";
import { useUpdateEntityTypeOrder } from "../../../data/useUpdateEntityTypeOrder";
import { notify } from "../../../utils/notify";
import { showError } from "../../../utils/error-handling";
import { queryClient } from "../../../queryClient";
import { entityTypeValidationSchema } from "../../../utils/validationSchema";

type RiskLower = "low" | "medium" | "high";
const riskOptions = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
] as const;

type EntityTypeRow = {
  id: number;
  name: string;
  order?: number | null;
  // Optional extras (if your API later supports them)
  risk_level?: RiskLower | string | null;
};

type DragState = {
  draggingId: number | null;
  overId: number | null;
};

type EntityTypePayload = {
  name: string;
  risk_level?: RiskLower;
};

const COLS = "minmax(320px,1fr) 180px 80px 30px";
const baseRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: COLS,
  alignItems: "center",
  columnGap: 24,
};
const headStyle: React.CSSProperties = {
  ...baseRow,
  padding: "10px 16px",
  color: theme.colors.muted,
};
const bodyRowStyle: React.CSSProperties = {
  ...baseRow,
  padding: "14px 16px",
  borderTop: "1px solid #F3F4F6",
};

const RiskPill = ({ value }: { value?: string | null }) => {
  const v = value.toLowerCase() as RiskLower;
  const label =
    v === "low"
      ? "Low"
      : v === "high"
      ? "High"
      : v === "medium"
      ? "Medium"
      : "-";
  const riskLevel = v === "low" ? "low" : v === "high" ? "high" : "medium";
  const bg = theme.colors.risk[riskLevel].bg;
  const color = theme.colors.risk[riskLevel].color;
  return (
    <Paragraph
      style={{
        display: "inline-block",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 600,
        background: bg,
        color,
        minWidth: "auto",
        textAlign: "center",
      }}
    >
      {label}
    </Paragraph>
  );
};

const Row = ({
  item,
  onEdit,
  draggableProps,
  isDragging,
}: {
  item: EntityTypeRow;
  onEdit: (item: EntityTypeRow) => void;
  draggableProps: {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragEnter: (e: React.DragEvent) => void;
    onDragEnd: (e: React.DragEvent) => void;
  };
  isDragging: boolean;
}) => {
  return (
    <div
      style={{
        ...bodyRowStyle,
        background: isDragging ? "#F9FAFB" : "transparent",
        cursor: "grab",
      }}
      {...draggableProps}
    >
      <Flex alignItems="center" gap="12">
        <BiGridVertical size={18} style={{ opacity: 0.7 }} />
        <Paragraph fontSize="2" fontWeight="600">
          {item.name}
        </Paragraph>
      </Flex>
      <RiskPill value={item.risk_level || "-"} />
      <Image
        src={editIcon}
        alt="edit icon"
        height="24px"
        width="24px"
        style={{ cursor: "pointer" }}
        onClick={() => onEdit(item)}
      />
      <span />
    </div>
  );
};

const SettingsPropertiesEntityTypes = () => {
  const { data: typesRes, isLoading, isError } = useEntityTypes();

  const { mutate: createType } = useCreateEntityType();
  const { mutate: editType } = useEditEntityType();
  const { mutate: deleteType } = useDeleteEntityType();
  const { mutate: updateOrder } = useUpdateEntityTypeOrder();

  const [list, setList] = useState<EntityTypeRow[]>([]);
  const items = useMemo<EntityTypeRow[]>(
    () =>
      Array.isArray(typesRes?.data)
        ? (typesRes!.data as any[]).map((t) => ({
            id: t.id,
            name: t.name,
            order: (t.order ?? null) as number | null,
            risk_level: (t.risk_level as RiskLower) ?? "-",
          }))
        : [],
    [typesRes]
  );
  useEffect(() => setList(items), [items]);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"add" | "edit">("add");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formName, setFormName] = useState("");
  const [formRisk, setFormRisk] = useState<RiskLower | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const [isReordering, setIsReordering] = useState(false);
  const dragState = useRef<DragState>({ draggingId: null, overId: null });

  const openAdd = () => {
    setFormMode("add");
    setEditingId(null);
    setFormName("");
    setFormRisk(null);
    setIsFormOpen(true);
  };

  const openEdit = (row: EntityTypeRow) => {
    setFormMode("edit");
    setEditingId(row.id);
    setFormName(row.name);
    setFormRisk(
      row.risk_level && row.risk_level !== "-"
        ? (row.risk_level.toLowerCase() as RiskLower)
        : null
    );
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (!isSubmitting) setIsFormOpen(false);
  };

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["entity_types"] });

  const onSuccess = (msg: string) => {
    notify(msg, { type: "success", position: "bottom-left" });
    setIsSubmitting(false);
    setIsFormOpen(false);
    setIsDeleteModalOpen(false);
    setConfirmingDelete(false);
    queryClient.invalidateQueries({
      queryKey: ["entity_type"],
    });
  };

  const onError = (err: any, msg: string) => {
    showError(err, msg);
    setIsSubmitting(false);
    setIsDeleteModalOpen(false);
    setConfirmingDelete(false);
  };

  const trimmedFormName = formName.trim();
  const formValues = { name: formName, risk_level: formRisk };
  const getValidationError = (field: "name" | "risk_level") => {
    try {
      entityTypeValidationSchema.validateSyncAt(field, formValues);
      return "";
    } catch (error) {
      return error instanceof Error ? error.message : "Invalid value.";
    }
  };
  const formNameError = getValidationError("name");
  const formRiskError = getValidationError("risk_level");
  const canSave =
    entityTypeValidationSchema.isValidSync(formValues) && !isSubmitting;

  const handleCreate = () => {
    if (!canSave) return;
    setIsSubmitting(true);

    const params: EntityTypePayload = { name: trimmedFormName };
    if (formRisk) params.risk_level = formRisk;

    createType(params, {
      onSuccess: () => onSuccess("Entity type added successfully!"),
      onError: (err) =>
        onError(err, "A problem occurred while creating the entity type."),
    });
  };

  const handleUpdate = () => {
    if (!editingId || !canSave) return;
    setIsSubmitting(true);

    const params: EntityTypePayload = { name: trimmedFormName };
    if (formRisk) params.risk_level = formRisk;

    editType(
      { params, id: editingId },
      {
        onSuccess: () => onSuccess("Entity type edited successfully!"),
        onError: (err: any) =>
          onError(err, "A problem occurred while updating the entity type."),
      }
    );
  };

  const handleDelete = () => {
    if (!editingId || isSubmitting) return;
    setIsSubmitting(true);
    setConfirmingDelete(true);

    deleteType(
      { id: editingId },
      {
        onSuccess: () => onSuccess("Entity type deleted successfully!"),
        onError: (err: any) =>
          onError(err, "A problem occurred while deleting the entity type."),
      }
    );
  };

  const reorderByIds = (
    arr: EntityTypeRow[],
    startId: number,
    endId: number
  ) => {
    const startIndex = arr.findIndex((x) => x.id === startId);
    const endIndex = arr.findIndex((x) => x.id === endId);
    if (startIndex === -1 || endIndex === -1 || startIndex === endIndex)
      return arr;
    const next = [...arr];
    const [moved] = next.splice(startIndex, 1);
    next.splice(endIndex, 0, moved);
    return next;
  };

  const handleDragStart = (id: number) => (e: React.DragEvent) => {
    dragState.current = { draggingId: id, overId: id };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(id));
  };
  const handleDragOver = (id: number) => (e: React.DragEvent) => {
    e.preventDefault();
    dragState.current.overId = id;
  };
  const handleDragEnter = (id: number) => (e: React.DragEvent) => {
    e.preventDefault();
    dragState.current.overId = id;
  };
  const handleDragEnd = () =>
    (dragState.current = { draggingId: null, overId: null });

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const { draggingId, overId } = dragState.current;
    if (!draggingId || !overId || draggingId === overId) return;

    const visibleIds = list.map((x) => x.id);
    if (!visibleIds.includes(draggingId) || !visibleIds.includes(overId))
      return;

    const reordered = reorderByIds(list, draggingId, overId);
    setList(reordered);

    setIsReordering(true);
    updateOrder(
      {
        orders: reordered.map((row, idx) => ({ id: row.id, order: idx })),
      },
      {
        onSuccess: () => {
          notify("Order updated!", {
            type: "success",
            position: "bottom-left",
          });
          setIsReordering(false);
          invalidate();
        },
        onError: (err: any) => {
          setIsReordering(false);
          showError(err, "Failed to update order.");
          invalidate();
        },
      }
    );

    dragState.current = { draggingId: null, overId: null };
  };

  return (
    <Box bg="white" borderRadius="16px" p={5} overflow="hidden">
      <Flex justifyContent="space-between" alignItems="center">
        <Box>
          <H2 fontSize="3">Entity Type Configuration</H2>
          <Paragraph color="gray.60">
            Configure the entity type classifications to match your
            organisation’s KYB requirements and workflows.
          </Paragraph>
        </Box>

        <Button Icon={FaPlus} iconSize="16px" onClick={openAdd}>
          Add New Entity Type
        </Button>
      </Flex>

      {isLoading ? (
        <Paragraph color="gray.60" mt={3}>
          Loading…
        </Paragraph>
      ) : isError ? (
        <Paragraph color="red.60" mt={3}>
          Failed to load entity types.
        </Paragraph>
      ) : (
        <>
          <div style={headStyle}>
            <Paragraph color="gray.60">Name</Paragraph>
            <Paragraph color="gray.60">Risk level</Paragraph>
            <Paragraph></Paragraph>
            <Paragraph></Paragraph>
          </div>

          {list.length === 0 ? (
            <Paragraph color="gray.60" px={4} py={4}>
              No entity types found.
            </Paragraph>
          ) : (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              style={{
                borderTop: "1px solid #F3F4F6",
                opacity: isReordering ? 0.6 : 1,
                pointerEvents: isReordering ? "none" : "auto",
              }}
            >
              {list.map((item) => (
                <Row
                  key={String(item.id)}
                  item={item}
                  onEdit={openEdit}
                  isDragging={dragState.current.draggingId === item.id}
                  draggableProps={{
                    draggable: true,
                    onDragStart: handleDragStart(item.id),
                    onDragOver: handleDragOver(item.id),
                    onDragEnter: handleDragEnter(item.id),
                    onDragEnd: handleDragEnd,
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      <Slider isOpen={isFormOpen} onRequestClose={closeForm} width="600px">
        <Box px={4} py={3}>
          <H2 mb={4}>
            {formMode === "add" ? "Add Entity Type" : "Edit Entity Type"}
          </H2>

          <Box mb={3}>
            <Paragraph mb={1} fontWeight="600">
              Name *
            </Paragraph>
            <Input
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Enter entity type name"
              isDisabled={isSubmitting}
              isRequired
              hasError={Boolean(formNameError)}
              errorMessage={formNameError}
            />
          </Box>

          <Box mb={3}>
            <Paragraph mb={1} fontWeight="600">
              Risk level *
            </Paragraph>
            <Select
              options={riskOptions as any}
              value={
                riskOptions.find((o) => o.value === formRisk) ?? null
              }
              onChange={(opt: any) => {
                const next = (opt?.value ?? null) as RiskLower | null;
                setFormRisk(next);
              }}
              width="100%"
              isDisabled={isSubmitting}
              hasError={Boolean(formRiskError)}
              errorMessage={formRiskError}
            />
          </Box>

          <Flex justifyContent="space-between" alignItems="center">
            {formMode === "edit" ? (
              <Button
                variant="danger"
                Icon={FaTrash}
                iconSize="12px"
                onClick={() => setIsDeleteModalOpen(true)}
                isDisabled={isSubmitting}
                iconColor="white"
              >
                Delete
              </Button>
            ) : (
              <span />
            )}

            <Flex gap="12" alignItems="center">
              <Button
                variant="secondary"
                onClick={closeForm}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={formMode === "add" ? handleCreate : handleUpdate}
                isDisabled={!canSave}
              >
                {isSubmitting ? "Saving…" : "Save"}
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Slider>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete entity type?"
        content="This will permanently remove the selected entity type."
        onModalConfirmClick={() => {
          if (isSubmitting) return;
          handleDelete();
        }}
        secondaryButtonText="Cancel"
        primaryButtonText={isSubmitting ? "Deleting…" : "Delete"}
        primaryButtonVariant="danger"
        hasSecondaryButton
        width="450px"
        doubleClick={confirmingDelete}
      />
    </Box>
  );
};

export default SettingsPropertiesEntityTypes;
